import { motion } from "framer-motion";
import { Check, Plus } from "lucide-react";
import { fadeUp, stagger } from "./motion";
import { FLAVORS, TOPPINGS, DRIZZLES, ASSETS } from "../../data/site";

const INCLUDES = ["Freshly Hand Rolled", "Premium Cream Base", "3 Complimentary Toppings", "Unlimited Drizzles"];
const featured = FLAVORS.filter((f) => f.img);

export const Menu = () => {
  return (
    <section id="menu" data-testid="menu-section" className="relative bg-[#FFF7F0]">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 sm:py-28">
        {/* header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div>
            <motion.span variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="eyebrow text-[#12B99B]">The Menu</motion.span>
            <motion.h2 variants={fadeUp} custom={1} initial="hidden" whileInView="show" viewport={{ once: true }}
              className="font-display mt-3 text-[clamp(2rem,6vw,4.5rem)] leading-[1] text-[#2A1620]">
              Fresh Hand Rolled <span className="font-script text-[#F63C8B]">Ice Cream</span>
            </motion.h2>
          </div>
          <motion.div variants={fadeUp} custom={2} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="shrink-0 card px-8 py-6 text-center">
            <span className="block text-xs font-bold uppercase tracking-[0.2em] text-[#12B99B]">Every Cup</span>
            <span className="font-display text-5xl sm:text-6xl text-[#F63C8B]">$10</span>
          </motion.div>
        </div>

        {/* includes */}
        <motion.ul variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {INCLUDES.map((inc) => (
            <motion.li key={inc} variants={fadeUp} className="card flex items-center gap-3 px-6 py-5">
              <span className="grid place-items-center h-9 w-9 rounded-full bg-[#E6FBF3] shrink-0">
                <Check className="text-[#12B99B]" size={18} strokeWidth={3} />
              </span>
              <span className="text-sm font-semibold text-[#2A1620]">{inc}</span>
            </motion.li>
          ))}
        </motion.ul>

        {/* all flavors as photo cards */}
        <div className="mt-16">
          <div className="flex items-baseline justify-between mb-6">
            <h3 className="font-display text-2xl sm:text-3xl text-[#2A1620]">All 25 Signature Flavors</h3>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#B79AA6]">Choose your roll</span>
          </div>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {FLAVORS.map((f, i) => (
              <motion.article key={f.name} variants={fadeUp} custom={i % 4} data-testid={`flavor-${i}`}
                className="card group overflow-hidden">
                <div className="overflow-hidden aspect-square rounded-t-[1.6rem] bg-gradient-to-br from-[#FFE9F1] to-[#E6FBF3]">
                  {f.img ? (
                    <img src={f.img} alt={f.name} loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-110" />
                  ) : (
                    <div className="w-full h-full grid place-items-center">
                      <img src={ASSETS.logoRed} alt="" aria-hidden="true" className="h-16 w-16 object-contain opacity-70 group-hover:rotate-12 transition-transform duration-500" />
                    </div>
                  )}
                </div>
                <div className="p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-2">
                    <h4 className="font-display text-sm sm:text-base leading-tight text-[#2A1620] group-hover:text-[#F63C8B] transition-colors">{f.name}</h4>
                    <span className="font-display text-xs text-[#F4C9DC] mt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <p className="mt-1.5 text-xs sm:text-sm text-[#8A7480] leading-snug">{f.desc}</p>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </div>

        {/* toppings + drizzles */}
        <div className="mt-16 grid lg:grid-cols-3 gap-10 lg:gap-14">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6 flex-wrap">
              <h3 className="font-display text-2xl sm:text-3xl text-[#2A1620]">Complimentary Toppings</h3>
              <span className="rounded-full bg-[#F63C8B] text-white text-xs font-bold px-3 py-1 uppercase tracking-wide">Choose any 3</span>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {TOPPINGS.map((t) => (
                <span key={t} data-testid={`topping-${t}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#F8DCE7] hover:border-[#F63C8B] text-[#3A2A32] text-sm px-4 py-2 shadow-[0_8px_18px_-14px_rgba(246,60,139,0.7)] transition-colors">
                  <Plus size={13} className="text-[#F63C8B]" /> {t}
                </span>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-3 mb-6">
              <h3 className="font-display text-2xl sm:text-3xl text-[#2A1620]">Drizzles</h3>
              <span className="rounded-full bg-[#E6FBF3] text-[#12B99B] text-xs font-bold px-3 py-1 uppercase tracking-wide">Unlimited</span>
            </div>
            <ul className="space-y-3">
              {DRIZZLES.map((d) => (
                <li key={d} className="flex items-center gap-3 border-b border-[#F4C9DC] pb-3 text-[#3A2A32]">
                  <span className="w-2 h-2 bg-[#F63C8B] rounded-full" /> {d}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};
